import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import CryptoJS from 'crypto-js';
import QrWithClinicLabel from '../../components/QrWithClinicLabel';
import KpiPager from '../../components/KpiPager';
import '../../styles/kpi-theme.css';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
    fetchAllClinicsForQrExport,
    fetchClinics
  } from '../../services/apiService';
  import {
    generateClinicQrBlob,
    safeQrFileName,
} from '../../utils/generateClinicQr';

interface ClinicsProps {
    setError: Dispatch<SetStateAction<string | null>>;
    setIsError: Dispatch<SetStateAction<boolean>>;
}

interface Clinic {
    id: number;
    rep: string | null;
    clinic_name: string | null;
}

interface ClinicRow extends Clinic {
    link: string;
}

// Experience link for the AstraZeneca game. `code` and `mail` are already
// encrypted + URL-encoded; each row appends its own encrypted clinic id.
const AZ_EXPERIENCE_LINK =
    'https://azapp.xorostory.com/?code=U2FsdGVkX19RA77c%2F%2Bdzzi2N3PyIX7hGfoiRHAbclfY%3D&mail=U2FsdGVkX1%2Fy6UhB%2BBpvf5iGLXy5GxvjJRYMcdgJpYjpmQ6k17G7kixxg5q0gmKi';
const CLINIC_PARAM = 'clinic_id';
const CLINIC_SECRET_KEY = 'clinic_id';
const GAME_NAME = 'Sugar Crush: Back To Target';

// Same encryption used on the old dashboard: AES with a per-parameter secret,
// then URL-encoded so the ciphertext survives being put in a query string.
const encryptString = (value: string, secretKey: string): string =>
    encodeURIComponent(CryptoJS.AES.encrypt(value, secretKey).toString());

const buildClinicLink = (clinicId: number): string =>
    `${AZ_EXPERIENCE_LINK}&${CLINIC_PARAM}=${encryptString(String(clinicId), CLINIC_SECRET_KEY)}`;

const Clinics: React.FC<ClinicsProps> = ({ setError, setIsError }) => {
    const [clinics, setClinics] = useState<Clinic[]>([]);
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [loading, setLoading] = useState(false);
    const [copiedId, setCopiedId] = useState<number | null>(null);

    // AES output is salted, so build each link once per fetch - otherwise every
    // re-render would produce a different ciphertext and redraw every QR code.
    const rows: ClinicRow[] = useMemo(
        () => clinics.map((clinic) => ({ ...clinic, link: buildClinicLink(clinic.id) })),
        [clinics]
    );

    // Debounce the search box so we don't fire a request per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(search.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);

            const downloadAllQrCodes = async () => {
                if (downloadingAll) {
                    return;
                }
            
                setDownloadingAll(true);
                setDownloadProgress(0);
            
                try {
                    const result = await fetchAllClinicsForQrExport();
                    const allClinics: Clinic[] = result.clinics || [];
            
                    if (allClinics.length === 0) {
                        setIsError(true);
                        setError('No clinics are available for QR download');
                        return;
                    }
            
                    const zip = new JSZip();
                    const qrFolder = zip.folder('clinic-qr-codes');
            
                    if (!qrFolder) {
                        throw new Error('Unable to create ZIP folder');
                    }
            
                    const usedFileNames = new Map<string, number>();
            
                    /*
                     * Generate sequentially to avoid creating hundreds of canvases
                     * simultaneously and freezing the browser.
                     */
                    for (let index = 0; index < allClinics.length; index += 1) {
                        const clinic = allClinics[index];
            
                        const clinicName =
                            clinic.clinic_name?.trim() || `Clinic_${clinic.id}`;
                        const repName =
                            clinic.rep?.trim() || 'No Rep';
            
                        const clinicLink = buildClinicLink(clinic.id);
            
                        const qrBlob = await generateClinicQrBlob({
                            value: clinicLink,
                            gameName: GAME_NAME,
                            clinicName,
                        });
                        
                        let baseFileName =
                            `${safeQrFileName(repName)}_${safeQrFileName(clinicName)}`;
                        
                        /*
                         * Handle clinics having the same name.
                         */
                        const existingCount = usedFileNames.get(baseFileName) || 0;
                        usedFileNames.set(baseFileName, existingCount + 1);
            
                        if (existingCount > 0) {
                            baseFileName = `${baseFileName}_${clinic.id}`;
                        }
            
                        qrFolder.file(`${baseFileName}.png`, qrBlob);
            
                        setDownloadProgress(
                            Math.round(((index + 1) / allClinics.length) * 100)
                        );
                    }
            
                    const zipBlob = await zip.generateAsync({
                        type: 'blob',
                        compression: 'DEFLATE',
                        compressionOptions: {
                            level: 6,
                        },
                    });
            
                    saveAs(zipBlob, 'Sugar-Crush-Clinic-QR-Codes.zip');
                } catch (err) {
                    setIsError(true);
                    setError(
                        err instanceof Error
                            ? err.message
                            : 'Unable to download clinic QR codes'
                    );
                } finally {
                    setDownloadingAll(false);
                    setDownloadProgress(0);
                }
            };

    const loadClinics = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchClinics(page, appliedSearch);
            setClinics(result.clinics || []);
            setTotalPages(result.total_pages || 1);
            setTotalCount(result.total_count || 0);
            setPerPage(result.per_page || 20);
        } catch (err) {
            setClinics([]);
            setTotalPages(1);
            setTotalCount(0);
            setIsError(true);
            setError(err instanceof Error ? err.message : 'Unable to load clinics');
        } finally {
            setLoading(false);
        }
    }, [page, appliedSearch, setError, setIsError]);

    useEffect(() => {
        loadClinics();
    }, [loadClinics]);

    const firstRow = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
    const lastRow = Math.min(page * perPage, totalCount);

    const copyLink = async (row: ClinicRow) => {
        try {
            await navigator.clipboard.writeText(row.link);
            setCopiedId(row.id);
            setTimeout(() => setCopiedId((current) => (current === row.id ? null : current)), 1500);
        } catch {
            setIsError(true);
            setError('Could not copy the link to your clipboard');
        }
    };

    return (
        <div className="kpi-scope">
            <main className="kpi-page">
                <section className="kpi-header">
                    <div className="kpi-title-wrap">
                        <h1>Clinics</h1>
                        <p>Browse every clinic, copy its link, or click a QR code to download it.</p>
                    </div>
                    <div className="kpi-project-pill">
                        <strong>{totalCount.toLocaleString()}</strong>
                        <span>Total clinics</span>
                    </div>
                </section>

                <section className="kpi-toolbar">
                    <input
                        className="kpi-search"
                        type="search"
                        placeholder="Search by clinic name or rep..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <button
                        type="button"
                        className="kpi-download-all-btn"
                        onClick={downloadAllQrCodes}
                        disabled={downloadingAll || totalCount === 0}
                    >
                        {downloadingAll
                            ? `Preparing QR ZIP... ${downloadProgress}%`
                            : 'Download All QRs'}
                    </button>
                </section>

                <section className="kpi-dashboard-card">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: '90px' }}>ID</th>
                                <th style={{ width: '180px' }}>Rep</th>
                                <th>Clinic Name</th>
                                <th style={{ width: '140px' }}>Link</th>
                                <th style={{ width: '110px' }}>QR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={5}><div className="kpi-state">Loading clinics...</div></td>
                                </tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={5}>
                                        <div className="kpi-state">
                                            {appliedSearch ? `No clinics match “${appliedSearch}”.` : 'No clinics found.'}
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!loading && rows.map((clinic) => (
                                <tr key={clinic.id}>
                                    <td className="kpi-row-id">{clinic.id}</td>
                                    <td>{clinic.rep || '—'}</td>
                                    <td>{clinic.clinic_name || '—'}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="kpi-copy-btn"
                                            onClick={() => copyLink(clinic)}
                                        >
                                            {copiedId === clinic.id ? 'Copied' : 'Copy Link'}
                                        </button>
                                    </td>
                                    <td>
                                        <QrWithClinicLabel
                                            value={clinic.link}
                                            gameName={GAME_NAME}
                                            clinicName={clinic.clinic_name || `Clinic ${clinic.id}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="kpi-table-meta">
                        <div>
                            {totalCount === 0
                                ? 'Showing 0 clinics'
                                : `Showing ${firstRow}–${lastRow} of ${totalCount.toLocaleString()} clinics`}
                        </div>
                        <KpiPager page={page} totalPages={totalPages} disabled={loading} onChange={setPage} />
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Clinics;
